import React from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import dayjs from "dayjs";
import {
  InternalCampaign,
  InsertionOrder,
} from "../../types/internalCampaignTypes";
import {
  resolveOrderLabel,
  resolveSourceCampaignId,
  resolveOrderMargin,
  resolveOrderUniqueId,
} from "../../utils/insertionOrderUtils";

type ViewInternalCampaignsProps = {
  campaignResponse:
    | {
        message?: string;
        data: components["schemas"]["CampaignWithRelations"] | null;
        status: number;
      }
    | undefined;
  isAdmin: boolean;
  insertionOrderMap: Record<string, InsertionOrder>;
};

export default function ViewInternalCampaigns({
  campaignResponse,
  isAdmin,
  insertionOrderMap,
}: ViewInternalCampaignsProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  if (
    campaignResponse &&
    campaignResponse?.data &&
    isAdmin &&
    campaignResponse.data.internalCampaigns &&
    campaignResponse.data.internalCampaigns.length > 0
  ) {
    return (
      <Grid item xs={12}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Admin Only - {t("campaigns.internalCampaigns")}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          {campaignResponse.data.internalCampaigns.map(
            (internalCampaign: InternalCampaign, index: number) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {internalCampaign.displayName ||
                    t("campaigns.internalCampaign")}{" "}
                  {index + 1}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t("campaigns.platform")}
                      </Typography>
                      <Typography variant="body1">
                        {internalCampaign.platform ||
                          internalCampaign.internalPlatform ||
                          t("campaigns.internalPlatform")}
                      </Typography>
                    </Box>
                  </Grid>

                  {isAdmin && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t("campaigns.campaignId")}
                        </Typography>
                        <Typography variant="body1">
                          {internalCampaign.campaignId || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {isAdmin && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t("campaigns.internalId")}
                        </Typography>
                        <Typography variant="body1">
                          {internalCampaign.internalId || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t("campaigns.startDate")}
                      </Typography>
                      <Typography variant="body1">
                        {internalCampaign.startDate
                          ? dayjs(internalCampaign.startDate).format(
                              "DD MMM YYYY"
                            )
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t("campaigns.endDate")}
                      </Typography>
                      <Typography variant="body1">
                        {internalCampaign.endDate
                          ? dayjs(internalCampaign.endDate).format(
                              "DD MMM YYYY"
                            )
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  {isAdmin && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t("campaigns.margin")}
                        </Typography>
                        <Typography variant="body1">
                          {internalCampaign.margin !== undefined
                            ? `${internalCampaign.margin}%`
                            : "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t("campaigns.timezone")}
                      </Typography>
                      <Typography variant="body1">
                        {internalCampaign.timezone || "UTC"}
                      </Typography>
                    </Box>
                  </Grid>

                  {internalCampaign.platform === "DV360" &&
                    internalCampaign.dv360InsertionOrderIds &&
                    internalCampaign.dv360InsertionOrderIds.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ mb: 1 }}
                        >
                          {t("campaigns.dv360InsertionOrders", {
                            defaultValue: "DV360 Insertion Orders",
                          })}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {internalCampaign.dv360InsertionOrderIds.map(
                            (orderId) => {
                              const order = insertionOrderMap[orderId];
                              const label = resolveOrderLabel(
                                order as InsertionOrder,
                                orderId
                              );
                              const subLabel = resolveOrderUniqueId(
                                order as InsertionOrder
                              );

                              return (
                                <Chip
                                  key={orderId}
                                  label={
                                    subLabel && subLabel !== label
                                      ? `${label} (${subLabel})`
                                      : label
                                  }
                                  variant="outlined"
                                  color="primary"
                                />
                              );
                            }
                          )}
                        </Box>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          {internalCampaign.dv360InsertionOrderIds.map(
                            (orderId) => {
                              const order = insertionOrderMap[orderId];
                              if (!order) {
                                return null;
                              }

                              const resolvedLabel = resolveOrderLabel(
                                order as InsertionOrder,
                                orderId
                              );
                              const resolvedId = resolveOrderUniqueId(
                                order as InsertionOrder
                              );
                              const resolvedMargin = resolveOrderMargin(
                                order as InsertionOrder
                              );
                              const resolvedSourceCampaignId =
                                resolveSourceCampaignId(
                                  order as InsertionOrder
                                );

                              return (
                                <Grid item xs={12} md={6} key={orderId}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      borderColor: theme.palette.grey[200],
                                      backgroundColor:
                                        theme.palette.background.default,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        mb: 0.5,
                                      }}
                                    >
                                      {resolvedLabel}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {t("campaigns.dv360InsertionOrderId", {
                                        defaultValue: "Insertion Order ID",
                                      })}
                                      : {resolvedId || "-"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {t(
                                        "campaigns.dv360InsertionOrderMargin",
                                        {
                                          defaultValue: "Margin",
                                        }
                                      )}
                                      :
                                      {typeof resolvedMargin === "number"
                                        ? ` ${resolvedMargin.toFixed(2)}%`
                                        : " -"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {t(
                                        "campaigns.dv360InsertionOrderAdvertiser",
                                        {
                                          defaultValue: "Advertiser ID",
                                        }
                                      )}
                                      : {order.advertiserId || "-"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {t(
                                        "campaigns.dv360InsertionOrderSource",
                                        {
                                          defaultValue: "Source Campaign ID",
                                        }
                                      )}
                                      : {resolvedSourceCampaignId || "-"}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              );
                            }
                          )}
                        </Grid>
                      </Grid>
                    )}

                  {/* Metrics Configuration Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      {t("campaigns.metricsConfiguration")}
                    </Typography>
                    <Grid container spacing={1}>
                      {internalCampaign.enableTotalInvestment && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.totalInvestment")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableTotalImpressions && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.totalImpressions")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableDailyCTR && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.dailyCTR")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableCompleteViews && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.completeViews")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableDailyImpressions && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.dailyImpressions")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableDailyInvestment && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.dailyInvestment")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableClicks && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.clicks")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableSites && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.sites")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableVTR && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.vtr")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableCPV && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.cpv")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableCTR && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.ctr")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableCPM && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.cpm")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                      {internalCampaign.enableCPC && (
                        <Grid item>
                          <Chip
                            label={t("campaigns.cpc")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {campaignResponse.data &&
                  campaignResponse.data.internalCampaigns &&
                  index <
                    campaignResponse.data.internalCampaigns.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
              </Box>
            )
          )}
        </Paper>
      </Grid>
    );
  }
}
